/**
 * HairlineTimeline — editorial hairline-thread timeline for the workshop
 * scarcity-display layer.
 *
 * Design source: Anthropic Cowork Hero handoff bundle, "Upcoming Dates —
 * Hairline Thread.html". This is the React adaptation, mapped to AdVenture
 * design tokens (--cream / --charcoal / --terra / --forest) and fonts
 * (--font-display, --font-mono).
 *
 * Renders 2-4 stops along a horizontal hairline that draws in left→right on
 * mount; stops fade up with delays sized to their positions so they appear
 * in sequence as the line passes them. Below md, the hairline collapses to
 * a vertical stacked list.
 */

// NOTE: hairline-timeline CSS lives in client/src/index.css (appended near
// the bottom). Component-level CSS imports trip the build because
// postcss.config still uses the Tailwind v3 plugin shape with Tailwind v4
// installed — until that's migrated to @tailwindcss/postcss, all CSS must
// flow through the entry index.css file.
import { displayCeilingFor, type ClockResult } from "./useSeatClock";

type SoldOutStop = {
  kind: "soldout";
  key: string;
  startDatetime: Date;
  timezone: string;
  /** When the stop represents a synthetic (non-real) past date, screen
   *  readers receive this label instead of the announced date. */
  ariaLabel?: string;
  /** End of the session, same meaning and same reason as OpenStop's. A sold-out
   *  card is the same object as an open one with the seats gone, so it carries
   *  the same "10:00 AM to 1:30 PM EDT on Zoom" line when a page asks for
   *  session meta (Isaac, 2026-09-09). Optional: the hairline itself has never
   *  had room for a time range, and a synthetic past tile only gets one because
   *  event-detail derives it from the event's own duration. */
  endDatetime?: Date;
  /** Where it ran, e.g. "Zoom" — the event row's own `platform`. */
  where?: string;
};

type OpenStop = {
  kind: "open";
  key: string;
  sessionId: number;
  startDatetime: Date;
  timezone: string;
  /** Clock result drives the capacity meter (tight vs open + count). */
  clock?: ClockResult;
  /** Per-series starting count, used for the "pre" meter copy. */
  startingCount: number;
  /** End of the session. Optional because only the premium date cards show a
   *  time range; the hairline itself has never had room for one. Passed as data
   *  rather than written into the card's copy so the two can never disagree —
   *  the session's real end is one edit away in the DB. */
  endDatetime?: Date;
  /** Where it runs, e.g. "Zoom" — the event row's own `platform`, not a
   *  hardcoded string, for the same reason. */
  where?: string;
};

export type HairlineStop = SoldOutStop | OpenStop;

type Props = {
  stops: HairlineStop[];
  onSelectSession?: (id: number) => void;
  /** Hide the per-date seat meter/count entirely (dates only, no numbers).
   *  Used by the Design page, which shows real dates without a seat count. */
  hideMeter?: boolean;
};

/** Evenly distribute N stops across the line, insetting from the fade edges. */
function distributePositions(n: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [50];
  const inset = 14;
  const gap = (100 - 2 * inset) / (n - 1);
  return Array.from({ length: n }, (_, i) => inset + i * gap);
}

const dayOfWeekFmt = (dt: Date, tz: string) =>
  dt.toLocaleDateString("en-US", { weekday: "long", timeZone: tz });

const monthDayFmt = (dt: Date, tz: string) =>
  dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: tz });

/** Map clock state → meter visual + copy. The 4-bar meter is driven by
 *  `clock.fillLevel` (max 3 — full bars never render; a maxed meter reads
 *  as zero scarcity). The text shifts from "open" wording at the widest
 *  state to "left" wording as the count decays. Last-call keeps the
 *  numeric count (the LAST CALL treatment is applied separately as an
 *  eyebrow on the stop). */
function meterFor(clock: ClockResult | undefined, startingCount: number): {
  fillLevel: 1 | 2 | 3 | 4;
  text: string;
} {
  // No clock yet (SSR / pre-mount): neutral wide-open — ceiling count, 3 bars.
  if (!clock) {
    return { fillLevel: 3, text: `${displayCeilingFor(startingCount)} seats open` };
  }
  if (clock.state === "sold-out") {
    // Defensive — sold-out should render as a soldout stop, not here.
    return { fillLevel: 1, text: "Sold out" };
  }
  // state === "clock" or "last-call": continuous decay. The widest state
  // (fillLevel 3) reads as "X seats open"; any further depletion shifts to
  // "X seats left" so the wording amplifies the visual drop. Last-call
  // usually lands at fillLevel 1.
  const seats = clock.seatsLeft ?? displayCeilingFor(startingCount);
  const text = clock.fillLevel >= 3 ? `${seats} seats open` : `${seats} seats left`;
  return { fillLevel: clock.fillLevel, text };
}

export function HairlineTimeline({ stops, onSelectSession, hideMeter = false }: Props) {
  const positions = distributePositions(stops.length);

  return (
    <div className="ht-wrap" aria-label="Upcoming workshop dates">
      {/* Desktop / md+: hairline + absolutely positioned stops */}
      <div className="ht-frame" aria-hidden={undefined}>
        <div className="ht-line" />
        {stops.map((stop, i) => {
          const pos = positions[i];
          // Delay the stop's fade so it lands just as the line passes its
          // position. Line draws over 1600ms with cubic ease; offset stops
          // proportionally so they appear in sequence as the line sweeps past.
          const delay = Math.round(pos * 13); // 0%→0ms, 100%→1300ms
          const stopStyle: React.CSSProperties = {
            left: `${pos}%`,
            ["--ht-stop-delay" as never]: `${delay}ms`,
          };

          if (stop.kind === "soldout") {
            return (
              <div
                key={stop.key}
                className="ht-stop ht-soldout"
                style={stopStyle}
                aria-label={stop.ariaLabel}
              >
                <div className="ht-mark">
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <line x1="3" y1="3" x2="11" y2="11" stroke="var(--terra)" strokeWidth="2.4" strokeLinecap="round" />
                    <line x1="11" y1="3" x2="3" y2="11" stroke="var(--terra)" strokeWidth="2.4" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="ht-below">
                  <div className="ht-dow" aria-hidden={stop.ariaLabel ? "true" : undefined}>
                    {dayOfWeekFmt(stop.startDatetime, stop.timezone)}
                  </div>
                  <div className="ht-date" aria-hidden={stop.ariaLabel ? "true" : undefined}>
                    {monthDayFmt(stop.startDatetime, stop.timezone)}
                  </div>
                  <span className="ht-soldbadge">Sold Out</span>
                </div>
              </div>
            );
          }

          const meter = meterFor(stop.clock, stop.startingCount);
          const isLastCall = stop.clock?.state === "last-call";
          const stopBody = (
            <>
              <div className="ht-mark" />
              <div className="ht-below">
                {isLastCall ? (
                  <div className="ht-lastcall-eyebrow">Last Call</div>
                ) : (
                  <div className="ht-dow">
                    {dayOfWeekFmt(stop.startDatetime, stop.timezone)}
                  </div>
                )}
                <div className="ht-date">
                  {monthDayFmt(stop.startDatetime, stop.timezone)}
                </div>
                {!hideMeter && (
                  <div className={`ht-meter ht-fill-${meter.fillLevel}`} aria-live="polite" aria-atomic="true">
                    <span className={`ht-bars ht-fill-${meter.fillLevel}`}>
                      <i /><i /><i /><i />
                    </span>
                    {meter.text}
                  </div>
                )}
              </div>
            </>
          );

          return (
            <div key={stop.key} className="ht-stop ht-open" style={stopStyle}>
              {onSelectSession ? (
                <button
                  type="button"
                  className="ht-stop-action"
                  onClick={() => onSelectSession(stop.sessionId)}
                  aria-label={`Reserve seat for ${dayOfWeekFmt(stop.startDatetime, stop.timezone)}, ${monthDayFmt(stop.startDatetime, stop.timezone)}`}
                >
                  {stopBody}
                </button>
              ) : (
                stopBody
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile / <md: vertical stacked list */}
      <div className="ht-mobile" role="list">
        {stops.map((stop) => {
          if (stop.kind === "soldout") {
            return (
              <div
                key={stop.key}
                className="ht-mobile-stop ht-soldout"
                role="listitem"
                aria-label={stop.ariaLabel}
              >
                <span className="ht-mobile-mark" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 14 14">
                    <line x1="3" y1="3" x2="11" y2="11" stroke="var(--terra)" strokeWidth="2.4" strokeLinecap="round" />
                    <line x1="11" y1="3" x2="3" y2="11" stroke="var(--terra)" strokeWidth="2.4" strokeLinecap="round" />
                  </svg>
                </span>
                <div className="ht-mobile-text">
                  <span className="ht-mobile-dow" aria-hidden={stop.ariaLabel ? "true" : undefined}>
                    {dayOfWeekFmt(stop.startDatetime, stop.timezone)}
                  </span>
                  <span className="ht-mobile-date" aria-hidden={stop.ariaLabel ? "true" : undefined}>
                    {monthDayFmt(stop.startDatetime, stop.timezone)}
                  </span>
                </div>
                <span className="ht-mobile-meta">
                  <span className="ht-soldbadge">Sold Out</span>
                </span>
              </div>
            );
          }

          const meter = meterFor(stop.clock, stop.startingCount);
          const isLastCall = stop.clock?.state === "last-call";
          const row = (
            <>
              <span className="ht-mobile-mark" aria-hidden="true" />
              <div className="ht-mobile-text">
                {isLastCall ? (
                  <span className="ht-lastcall-eyebrow">Last Call</span>
                ) : (
                  <span className="ht-mobile-dow">
                    {dayOfWeekFmt(stop.startDatetime, stop.timezone)}
                  </span>
                )}
                <span className="ht-mobile-date">
                  {monthDayFmt(stop.startDatetime, stop.timezone)}
                </span>
              </div>
              {!hideMeter && (
                <span className="ht-mobile-meta">
                  <div className={`ht-meter ht-fill-${meter.fillLevel}`} aria-live="polite" aria-atomic="true">
                    <span className={`ht-bars ht-fill-${meter.fillLevel}`}>
                      <i /><i /><i /><i />
                    </span>
                    {meter.text}
                  </div>
                </span>
              )}
            </>
          );

          return (
            <div
              key={stop.key}
              className="ht-mobile-stop ht-open"
              role="listitem"
            >
              {onSelectSession ? (
                <button
                  type="button"
                  onClick={() => onSelectSession(stop.sessionId)}
                  className="ht-stop-action"
                  style={{ display: "contents" }}
                  aria-label={`Reserve seat for ${dayOfWeekFmt(stop.startDatetime, stop.timezone)}, ${monthDayFmt(stop.startDatetime, stop.timezone)}`}
                >
                  {row}
                </button>
              ) : (
                row
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
